import {
  createContext, useContext, useEffect, useRef, useState, type ReactNode,
} from 'react';
import { NavLink, useLocation as useRouterLocation } from 'react-router-dom';
import { cx } from '../lib/cx';
import { IconButton } from './IconButton';
import type { IconComponent } from '../icons';
import { Icons } from '../icons-export';

const ShellRailContext = createContext<{ expanded: boolean }>({ expanded: false });

export function useShellRail() {
  return useContext(ShellRailContext);
}

export interface RailItem {
  to: string;
  label: string;
  icon: IconComponent;
  end?: boolean;
  children?: RailItem[];
}

const STORAGE_KEY = 'dhuttu.shell.railExpanded';

function readInitialExpanded(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

export function Shell({
  children,
  railHeader,
  railTop,
  railBottom = [],
  railFooter,
  topBar,
}: {
  children: ReactNode;
  /** Logo / brand slot rendered at the very top of the sidebar. */
  railHeader?: ReactNode;
  railTop: RailItem[];
  railBottom?: RailItem[];
  railFooter?: ReactNode;
  /** Optional content rendered as a sticky bar above the routed canvas. */
  topBar?: ReactNode;
}) {
  const [expanded, setExpanded] = useState<boolean>(readInitialExpanded);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, expanded ? '1' : '0');
    } catch {
      // localStorage not available — silently ignore.
    }
  }, [expanded]);

  return (
    <ShellRailContext.Provider value={{ expanded }}>
      <div className="theme-v2 v2-bg v2-bg-pattern h-screen w-screen overflow-hidden">
        <div className="flex h-full gap-3 p-3">
          <SidebarRail
            header={railHeader}
            top={railTop}
            bottom={railBottom}
            footer={railFooter}
            expanded={expanded}
            onToggle={() => setExpanded((e) => !e)}
          />
          <div className="flex min-w-0 flex-1 flex-col gap-3">
            {topBar}
            <main className="flex-1 overflow-y-auto">{children}</main>
          </div>
        </div>
      </div>
    </ShellRailContext.Provider>
  );
}

function SidebarRail({
  header,
  top,
  bottom,
  footer,
  expanded,
  onToggle,
}: {
  header?: ReactNode;
  top: RailItem[];
  bottom: RailItem[];
  footer?: ReactNode;
  expanded: boolean;
  onToggle: () => void;
}) {
  return (
    <aside
      className={cx(
        'v2-card relative flex h-full shrink-0 flex-col justify-between py-3 transition-[width] duration-200 ease-out',
        expanded ? 'w-[228px] px-2' : 'w-[44px] items-center px-1',
      )}
      aria-label="Primary navigation"
    >
      <CollapseToggle expanded={expanded} onClick={onToggle} />

      <div className="flex flex-col gap-2">
        {header && (
          <div className={cx('flex', expanded ? 'justify-start px-1.5 pt-1' : 'justify-center pt-1')}>
            {header}
          </div>
        )}
        <div className={cx('flex flex-col gap-1.5', expanded ? 'items-stretch' : 'items-center')}>
          {top.map((item) => (
            <RailNode key={item.to} item={item} expanded={expanded} />
          ))}
        </div>
      </div>

      <div className={cx('flex flex-col gap-1.5', expanded ? 'items-stretch' : 'items-center')}>
        {bottom.map((item) => (
          <RailNode key={item.to} item={item} expanded={expanded} />
        ))}
        {footer}
      </div>
    </aside>
  );
}

function CollapseToggle({ expanded, onClick }: { expanded: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={expanded ? 'Collapse sidebar' : 'Expand sidebar'}
      aria-expanded={expanded}
      title={expanded ? 'Collapse sidebar' : 'Expand sidebar'}
      className={cx(
        'absolute -right-2 top-3 z-20 grid h-4 w-4 place-items-center rounded-full',
        'bg-[color:var(--v2-ink)] text-white shadow-[0_2px_6px_-2px_rgba(20,25,42,0.5)] ring-2 ring-[color:var(--v2-bg-via)] transition',
        'hover:bg-[color:var(--v2-ink-soft)]',
      )}
    >
      <Icons.ChevronRight
        className={cx('h-2.5 w-2.5 transition-transform', expanded ? 'rotate-180' : '')}
      />
    </button>
  );
}

function RailNode({ item, expanded }: { item: RailItem; expanded: boolean }) {
  const hasChildren = !!item.children && item.children.length > 0;
  if (!hasChildren) return <RailLink item={item} expanded={expanded} />;
  return expanded
    ? <RailGroupExpanded item={item} />
    : <RailGroupCollapsed item={item} />;
}

function RailLink({ item, expanded }: { item: RailItem; expanded: boolean }) {
  return (
    <NavLink to={item.to} end={item.end} className="group relative block">
      {({ isActive }) => (
        expanded
          ? <ExpandedRow item={item} isActive={isActive} />
          : <CollapsedRow item={item} isActive={isActive} />
      )}
    </NavLink>
  );
}

function ExpandedRow({ item, isActive }: { item: RailItem; isActive: boolean }) {
  return (
    <div
      aria-current={isActive ? 'page' : undefined}
      className={cx(
        'flex items-center gap-2 rounded-md px-2 py-1.5 transition',
        isActive
          ? 'bg-[color:var(--v2-accent)] text-[color:var(--v2-accent-ink)] shadow-[0_4px_10px_-4px_rgba(20,25,42,0.5)]'
          : 'text-[color:var(--v2-ink-soft)] hover:bg-white/70 hover:text-[color:var(--v2-ink)]',
      )}
    >
      <item.icon className="h-[17px] w-[17px] shrink-0" />
      <span className="truncate text-xs font-semibold">{item.label}</span>
    </div>
  );
}

function CollapsedRow({ item, isActive }: { item: RailItem; isActive: boolean }) {
  return (
    <>
      <IconButton active={isActive} aria-current={isActive ? 'page' : undefined} aria-label={item.label}>
        <item.icon className="h-[17px] w-[17px]" />
      </IconButton>
      <span
        role="tooltip"
        className="pointer-events-none absolute left-[calc(100%+10px)] top-1/2 z-50 -translate-y-1/2 whitespace-nowrap rounded-md bg-[color:var(--v2-ink)] px-2 py-1 text-[11px] font-semibold text-white opacity-0 shadow-lg ring-1 ring-black/10 transition-opacity duration-100 group-hover:opacity-100"
      >
        {item.label}
      </span>
    </>
  );
}

function useIsAnyChildActive(item: RailItem): boolean {
  const loc = useRouterLocation();
  return (item.children ?? []).some((c) => matchesPath(loc.pathname, c.to, c.end));
}

function matchesPath(pathname: string, to: string, end?: boolean): boolean {
  if (end) return pathname === to;
  return pathname === to || pathname.startsWith(to + '/') || pathname.startsWith(to + '?');
}

function RailGroupExpanded({ item }: { item: RailItem }) {
  const anyChildActive = useIsAnyChildActive(item);
  const [open, setOpen] = useState<boolean>(anyChildActive);

  // Re-open the group when the user navigates to a child route.
  useEffect(() => {
    if (anyChildActive) setOpen(true);
  }, [anyChildActive]);

  return (
    <div className="flex flex-col">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className={cx(
          'flex items-center gap-2 rounded-md px-2 py-1.5 text-left transition',
          anyChildActive
            ? 'text-[color:var(--v2-ink)]'
            : 'text-[color:var(--v2-ink-soft)] hover:bg-white/70 hover:text-[color:var(--v2-ink)]',
        )}
      >
        <item.icon className="h-[17px] w-[17px] shrink-0" />
        <span className="flex-1 truncate text-xs font-semibold">{item.label}</span>
        <Icons.ChevronRight
          className={cx('h-3 w-3 shrink-0 transition-transform', open ? 'rotate-90' : '')}
          aria-hidden
        />
      </button>
      {open && (
        <ul className="ml-3 mt-0.5 flex flex-col gap-0.5 border-l border-[color:var(--v2-stroke-2)] pl-1.5">
          {item.children!.map((child) => (
            <li key={child.to}>
              <NavLink to={child.to} end={child.end} className="group relative block">
                {({ isActive }) => (
                  <div
                    className={cx(
                      'flex items-center gap-2 rounded-md px-2 py-1 transition',
                      isActive
                        ? 'bg-[color:var(--v2-accent)] text-[color:var(--v2-accent-ink)]'
                        : 'text-[color:var(--v2-ink-soft)] hover:bg-white/70 hover:text-[color:var(--v2-ink)]',
                    )}
                  >
                    <child.icon className="h-[14px] w-[14px] shrink-0" />
                    <span className="truncate text-[11px] font-semibold">{child.label}</span>
                  </div>
                )}
              </NavLink>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function RailGroupCollapsed({ item }: { item: RailItem }) {
  const anyChildActive = useIsAnyChildActive(item);
  const [open, setOpen] = useState(false);
  const closeTimer = useRef<number | null>(null);

  const clearCloseTimer = () => {
    if (closeTimer.current !== null) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };
  const scheduleClose = () => {
    clearCloseTimer();
    // Debounce: keep the panel open briefly so the cursor can travel from the
    // trigger across the gap into the panel without losing focus.
    closeTimer.current = window.setTimeout(() => setOpen(false), 180);
  };
  const openNow = () => {
    clearCloseTimer();
    setOpen(true);
  };

  useEffect(() => () => clearCloseTimer(), []);

  return (
    <div
      className="relative"
      onMouseEnter={openNow}
      onMouseLeave={scheduleClose}
      onFocus={openNow}
      onBlur={scheduleClose}
    >
      <IconButton active={anyChildActive} aria-label={item.label} aria-expanded={open}>
        <item.icon className="h-[17px] w-[17px]" />
      </IconButton>
      {/* Invisible bridge that keeps the hover region continuous between the
       * trigger icon and the floating panel — so the panel stays open while the
       * cursor crosses the 10px gap. */}
      <div
        aria-hidden
        className={cx(
          'absolute left-full top-0 z-40 h-full w-[10px]',
          open ? 'pointer-events-auto' : 'pointer-events-none',
        )}
      />
      <div
        role="menu"
        aria-label={item.label}
        className={cx(
          'absolute left-[calc(100%+10px)] top-1/2 z-50 -translate-y-1/2 min-w-[180px] rounded-md bg-[color:var(--v2-ink)] p-1 shadow-lg ring-1 ring-black/10 transition-opacity duration-100',
          open ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0',
        )}
      >
        <div className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-white/60">
          {item.label}
        </div>
        <ul className="flex flex-col">
          {item.children!.map((child) => (
            <li key={child.to}>
              <NavLink
                to={child.to}
                end={child.end}
                className={({ isActive }) =>
                  cx(
                    'flex items-center gap-2 rounded-md px-2 py-1.5 text-[11px] font-semibold transition',
                    isActive
                      ? 'bg-[color:var(--v2-accent)] text-[color:var(--v2-accent-ink)]'
                      : 'text-white/85 hover:bg-white/10',
                  )
                }
              >
                <child.icon className="h-[14px] w-[14px] shrink-0" />
                <span className="truncate">{child.label}</span>
              </NavLink>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
